import { v4 as uuidv4 } from 'uuid';
import { sleep } from './async';

/* 
    ::::: Usage example :::::

    import createQueue from 'modules/helpers/queue';

    const queue = createQueue({idle: [true, false]});

        const job3 = () =>
            new Promise(resolve => {
                console.log('job3');
                resolve('this is job3s resolve data');
            });

        job3.timeout = 2000;
        job3.onComplete = jobPromise => {
            console.log('job3 complete - wuhu', jobPromise);
        };

        queue.push(
            function job1() {
                return new Promise(resolve => {
                    console.log('job1');
                    resolve();
                });
            },
            function job2() {
                return new Promise(resolve => {
                    setTimeout(() => {
                        console.log('job2');
                        resolve();
                    }, 100);
                });
            },
            job3
        );


        queue.start();

        // Or if you want to finish the queue before giong on
        await queue.start();

*/

interface IQueueObject {
    queue: (() => void)[];
    unshift: (...any) => IJob[];
    push: (...any) => IJob[];
    remove: (id: string) => void;
    start: () => Promise<void>;
    cancel: () => void;
}

interface IJob {
    id: string;
    complete: boolean;
}

interface ICreateQueue {
    idle?: boolean;
    idleTimeout?: number | undefined;
}

export default function createQueue(options: ICreateQueue = {}): IQueueObject {
    const { idle = false, idleTimeout } = options;
    const queue = [];

    function assignProps(...jobs): IJob[] {
        return jobs.map((job): IJob => {
            const id = uuidv4();
            job.id = id;
            job.complete = false;
            return job;
        });
    }

    function unshift(...jobs): IJob[] {
        const jobsWithProps = assignProps(...jobs);

        queue.unshift(...jobsWithProps);

        return jobsWithProps;
    }

    function push(...jobs): IJob[] {
        const jobsWithProps = assignProps(...jobs);

        queue.push(...jobsWithProps);

        return jobsWithProps;
    }

    function remove(id): void {
        const index = queue.findIndex((job): boolean => job.id === id);

        if (index !== -1) {
            queue.splice(index, 1);
        } else {
            throw new Error(`Could not find job with ID: ${id} in queue`);
        }
    }

    async function processJob(job): Promise<void> {
        try {
            const jobResponse = await job();
            job.complete = true;
            if (job.onComplete) {
                job.onComplete(jobResponse);
            }
        } catch (err) {
            console.error(`Job rejected with the following error: ${err}`);
        } finally {
            remove(job.id);
        }
    }

    async function processQueue(): Promise<void> {
        if (!queue.length) {
            // console.info('job queue finished');
            return;
        }

        const job = queue[0];
        const { timeout = 0 } = job;

        if (timeout) {
            await sleep(timeout);
        }

        await processJob(job);
        await processQueue();
    }

    async function processQueueIdle(deadline): Promise<void> {
        while ((deadline.timeRemaining() > 2 || deadline.didTimeout) && queue.length) {
            // eslint-disable-next-line no-await-in-loop
            await processJob(queue[0]);
        }

        if (queue.length) {
            window.requestIdleCallback(processQueueIdle, { timeout: idleTimeout });
        } else {
            // console.info('Idle job queue finished');
        }
    }

    async function start(): Promise<void> {
        if (!queue.length) {
            throw new Error('No jobs in queue');
        }

        if (idle) {
            window.requestIdleCallback(processQueueIdle, { timeout: idleTimeout });
        } else {
            await processQueue();
        }
    }

    function cancel(): void {
        queue.forEach((job): void => {
            remove(job.id);
        });
    }

    return {
        queue,
        unshift,
        push,
        remove,
        start,
        cancel
    };
}
